import { vn } from 'vend-number'
import { FulfilmentType } from './fulfilment'
import { LineItem } from './line-item'

/**
 * Numeric obligation type sent to the API on `GqlLineItem.obligationType` as a line-item attribute.
 * Maps to the obligation_type value stored in vend_register_sale_product_attribute.
 *
 * NONE and SERVICE both correspond to FulfilmentType.UNSPECIFIED at the LineItem level today.
 * SERVICE is distinguished via the sale-level saleHasServiceOrder flag
 */
export enum ObligationType {
  TAKE_NOW = 0, // Take-now — item given to the customer immediately at point of sale
  PICKUP = 1, // Customer will collect the item later
  DISPATCH = 2, // Item will be shipped/dispatched to the customer
  SERVICE = 3, // Item is part of a service job
}

export class ObligationResolver {
  private readonly obligationType: ObligationType | null

  constructor(lineItem: LineItem, saleHasServiceOrder: boolean) {
    this.obligationType = this.resolveObligationType(
      lineItem,
      saleHasServiceOrder
    )
  }

  /**
   * Resolve the obligation_type for a line item.
   *
   * The value maps to the vend_register_sale_product_attribute obligation_type attribute.
   *
   * PICKUP and DELIVERY are resolved from the line item's own fulfilmentType.
   */
  private resolveObligationType(
    lineItem: LineItem,
    saleHasServiceOrder: boolean
  ): ObligationType | null {
    if (lineItem.isReturn || !vn(lineItem.quantity).greaterThan(0)) return null
    if (saleHasServiceOrder) return ObligationType.SERVICE
    if (lineItem.fulfilmentType === FulfilmentType.PICKUP)
      return ObligationType.PICKUP
    if (lineItem.fulfilmentType === FulfilmentType.DELIVERY)
      return ObligationType.DISPATCH
    return ObligationType.TAKE_NOW
  }

  /**
   * Checks if the resolved obligation type is TAKE_NOW
   *
   * @returns boolean - true if the obligation type resolved to TAKE_NOW
   */
  public isTakeNow(): boolean {
    return this.obligationType === ObligationType.TAKE_NOW
  }

  /**
   * Checks if the resolved obligation type is DISPATCH or PICKUP
   *
   * @returns boolean - true if the obligation type resolved to DISPATCH or PICKUP
   */
  public isFulfilment(): boolean {
    return (
      this.obligationType === ObligationType.DISPATCH ||
      this.obligationType === ObligationType.PICKUP
    )
  }

  public isService(): boolean {
    return this.obligationType === ObligationType.SERVICE
  }

  /**
   * Checks if the obligation type is eligible for recognition
   *
   * @returns boolean - true if the obligation type resolved to DISPATCH or PICKUP
   */
  public isEligibleForRecognition(): boolean {
    return this.obligationType !== null
  }

  /**
   * Getter for obligationType.
   * @returns ObligationType
   */
  public getObligationType(): ObligationType | null {
    return this.obligationType
  }
}
